/**
 * Pure geometry for the automatic page crop on the Upload Photos step.
 *
 * Every interior photo is cropped to the printed page ratio before it is
 * shown in the grid or sent for line art. That crop used to be centred; the
 * smart crop centres the same-sized window on the faces instead.
 *
 * Only the *position* changes. The window is always the largest rectangle
 * of the page ratio that fits inside the photo, so the output never loses
 * resolution compared with the centre crop, and the ratio is exact by
 * construction. Unusable input makes these helpers return null so the
 * caller falls back to the centre crop.
 *
 * All rectangles are in source-image pixels, the same frame the cropper
 * (cropperjs `setData`) and Review Book's regenerate path use.
 */

/** Full-bleed page: 8.75in × 11.5in. */
export const PAGE_ASPECT_RATIO = 8.75 / 11.5;

const isPositiveFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;
const isFinite_ = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * Largest width/height of `ratio` that fits inside imageWidth × imageHeight.
 * Returns null when the image dimensions are unusable.
 */
export function largestCropSize(imageWidth, imageHeight, ratio = PAGE_ASPECT_RATIO) {
  if (!isPositiveFinite(imageWidth) || !isPositiveFinite(imageHeight) || !isPositiveFinite(ratio)) {
    return null;
  }
  if (imageWidth / imageHeight > ratio) {
    // Wider than the page: full height, trim the sides.
    return { width: imageHeight * ratio, height: imageHeight };
  }
  // Taller than (or equal to) the page: full width, trim top and bottom.
  return { width: imageWidth, height: imageWidth / ratio };
}

/**
 * The page-ratio window centred on (focusX, focusY), pushed back inside the
 * image if the focus is near an edge. Null when the image is unusable.
 */
export function cropRectAroundFocus(focusX, focusY, imageWidth, imageHeight, ratio = PAGE_ASPECT_RATIO) {
  const size = largestCropSize(imageWidth, imageHeight, ratio);
  if (!size || !isFinite_(focusX) || !isFinite_(focusY)) return null;
  const x = clamp(focusX - size.width / 2, 0, imageWidth - size.width);
  const y = clamp(focusY - size.height / 2, 0, imageHeight - size.height);
  return { x, y, width: size.width, height: size.height };
}

/** The crop the step has always used: the page-ratio window in the middle. */
export function centerCropRect(imageWidth, imageHeight, ratio = PAGE_ASPECT_RATIO) {
  return cropRectAroundFocus(imageWidth / 2, imageHeight / 2, imageWidth, imageHeight, ratio);
}

/**
 * The smallest box containing every box in `boxes` ({x, y, width, height}),
 * ignoring malformed entries. Null when none are usable.
 */
export function unionOfBoxes(boxes) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  (boxes || []).forEach((b) => {
    if (!b || !isFinite_(b.x) || !isFinite_(b.y) || !isPositiveFinite(b.width) || !isPositiveFinite(b.height)) return;
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width);
    y1 = Math.max(y1, b.y + b.height);
  });
  if (!Number.isFinite(x0) || !Number.isFinite(x1)) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Zoom settings for the face-centred crop. The window only shrinks when the
 * subject is small in the frame, never below a print-safe size.
 */
export const ZOOM = Object.freeze({
  /** Face boxes this many face-heights tall, measured from the face top, stand in for the body. */
  bodyHeightsBelowFace: 8.5,
  /** Hair, hats and a taller person behind, above the face box, in face heights. */
  aboveFace: 2,
  /** Shoulders and arms either side of the faces, in face widths. */
  besideFace: 1.5,
  /** Breathing room around the subject box, as a fraction of its size. */
  margin: 0.15,
  /** No zoom when the subject already fills this share of the full window. */
  noZoomAbove: 0.9,
  /** Never zoom in more than this. */
  maxZoom: 1.7,
  /** Never leave fewer source pixels than this across the window. */
  minWindowWidth: 1100,
});

/**
 * A box around the people, estimated from their face boxes: each face is
 * extended down to where feet would be for someone standing, out to the
 * shoulders and up over the hair, then all are unioned and clamped to the
 * image. Null without usable faces.
 */
export function subjectBoxFromFaces(faces, imageWidth, imageHeight, z = ZOOM) {
  if (!isPositiveFinite(imageWidth) || !isPositiveFinite(imageHeight)) return null;
  const boxes = (faces || [])
    .filter((f) => f && isFinite_(f.x) && isFinite_(f.y) && isPositiveFinite(f.width) && isPositiveFinite(f.height))
    .map((f) => ({
      x: f.x - f.width * z.besideFace,
      y: f.y - f.height * z.aboveFace,
      width: f.width * (1 + 2 * z.besideFace),
      height: f.height * (z.aboveFace + z.bodyHeightsBelowFace),
    }));
  const u = unionOfBoxes(boxes);
  if (!u) return null;
  const x0 = Math.max(0, u.x);
  const y0 = Math.max(0, u.y);
  const x1 = Math.min(imageWidth, u.x + u.width);
  const y1 = Math.min(imageHeight, u.y + u.height);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * The page window for a subject box: full size and centred on the subject
 * when the subject already fills most of it, otherwise shrunk (keeping the
 * page ratio) until the subject plus margin fills it, within maxZoom and
 * the print-safe minimum width. Null when the image is unusable.
 */
export function zoomedCropRect(subject, imageWidth, imageHeight, ratio = PAGE_ASPECT_RATIO, z = ZOOM) {
  const full = largestCropSize(imageWidth, imageHeight, ratio);
  if (!full) return null;
  if (!subject) return centerCropRect(imageWidth, imageHeight, ratio);
  const cx = subject.x + subject.width / 2;
  const cy = subject.y + subject.height / 2;
  const needW = subject.width * (1 + 2 * z.margin);
  const needH = subject.height * (1 + 2 * z.margin);
  // Share of the full window the subject needs, in its limiting dimension.
  let scale = Math.max(needW / full.width, needH / full.height);
  if (!Number.isFinite(scale) || scale >= z.noZoomAbove) {
    return cropRectAroundFocus(cx, cy, imageWidth, imageHeight, ratio);
  }
  const floor = Math.max(1 / z.maxZoom, z.minWindowWidth / full.width);
  scale = Math.min(1, Math.max(scale, floor));
  const width = full.width * scale;
  const height = full.height * scale;
  const clamp = (n, a, b) => (b < a ? a : Math.min(Math.max(n, a), b));
  return {
    x: clamp(cx - width / 2, 0, imageWidth - width),
    y: clamp(cy - height / 2, 0, imageHeight - height),
    width,
    height,
  };
}

/**
 * Thresholds for "worth a look" flags on an automatic crop. Rule-based so
 * every flag can say why; tuned on a set of real family photos.
 */
export const REVIEW = Object.freeze({
  /** A face with more than this share of its box outside the window is clipped. */
  faceClippedShare: 0.1,
  /** Largest face shorter than this share of the image height: a wide shot, faces may be missed. */
  tinyFaceHeight: 0.03,
  /** This many faces or more: a crowd, the page cannot hold everyone. */
  crowd: 5,
  /** Faces the detector was unsure about are used only here: this many of them outside the window. */
  possibleFacesOutside: 1,
  /**
   * Detection confidence and skin-toned detail outside the window are
   * recorded in the metrics but do not flag: on real photos BlazeFace scores
   * good faces anywhere from 0.5 up, and gravel, sand, pavement and beige
   * walls all read as skin.
   */
});

/**
 * Plain measurements of an automatic crop, for the review rules and the
 * logs. `faces`, `subject` and `rect` are in image pixels; `skin` is the
 * {inside, outside} sum of skin-toned detail relative to the window.
 */
export function cropReviewMetrics({ faces, possibleFaces, subject, rect, imageWidth, imageHeight, skin, zoom }) {
  const usable = (list) => (list || []).filter((f) => f && isPositiveFinite(f.width) && isPositiveFinite(f.height));
  const fs = usable(faces);
  const possible = usable(possibleFaces);
  const share = (f) => {
    if (!rect) return 0;
    const ix = Math.max(0, Math.min(f.x + f.width, rect.x + rect.width) - Math.max(f.x, rect.x));
    const iy = Math.max(0, Math.min(f.y + f.height, rect.y + rect.height) - Math.max(f.y, rect.y));
    return 1 - (ix * iy) / (f.width * f.height);
  };
  const skinTotal = skin ? skin.inside + skin.outside : 0;
  return {
    faceCount: fs.length,
    minFaceScore: fs.length ? Math.min(...fs.map((f) => (Number.isFinite(f.score) ? f.score : 1))) : null,
    largestFaceHeight: fs.length && isPositiveFinite(imageHeight) ? Math.max(...fs.map((f) => f.height)) / imageHeight : null,
    maxFaceClipped: fs.length ? Math.max(...fs.map(share)) : 0,
    subjectWiderThanWindow: !!(subject && rect && (subject.width > rect.width * 1.02 || subject.height > rect.height * 1.02)),
    skinOutside: skinTotal > 0 ? skin.outside / skinTotal : null,
    possibleFaceCount: possible.length,
    possibleFacesOutside: possible.filter((f) => share(f) > 0.5).length,
    zoom: Number.isFinite(zoom) ? zoom : 1,
  };
}

/**
 * Which crops a customer should glance at, and why. `detectorAvailable`
 * false means the fallback centre crop was used for a reason that has
 * nothing to do with the photo, so it is recorded but not flagged.
 */
export function cropReviewFlags(m, { detectorAvailable = true } = {}, t = REVIEW) {
  const reasons = [];
  if (!m) return { flagged: false, reasons };
  if (!detectorAvailable) return { flagged: false, reasons: ['no-detector'] };
  if (m.faceCount === 0) reasons.push('no-face');
  if (m.faceCount >= t.crowd) reasons.push('crowd');
  if (m.faceCount > 0 && m.largestFaceHeight !== null && m.largestFaceHeight < t.tinyFaceHeight) reasons.push('tiny-faces');
  if (m.maxFaceClipped > t.faceClippedShare) reasons.push('face-clipped');
  // One person's estimated body can overrun the window on a close-up without
  // anyone being cut off; with two or more faces the group really is wider.
  if (m.subjectWiderThanWindow && m.faceCount >= 2) reasons.push('wider-than-page');
  if ((m.possibleFacesOutside || 0) >= t.possibleFacesOutside) reasons.push('possible-people-outside');
  return { flagged: reasons.length > 0, reasons };
}

/**
 * Skin-toned detail inside and outside `rect`, from RGBA pixels (the same
 * skin colour and threshold smartcrop.js uses). Cheap on the small copy.
 */
export function skinSplit(data, width, height, rect) {
  let inside = 0;
  let outside = 0;
  if (!data || !isPositiveFinite(width) || !isPositiveFinite(height)) return { inside, outside };
  const rx0 = rect ? rect.x : 0, ry0 = rect ? rect.y : 0;
  const rx1 = rect ? rect.x + rect.width : width, ry1 = rect ? rect.y + rect.height : height;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const mag = Math.sqrt(r * r + g * g + b * b) || 1;
      const d = Math.sqrt((r / mag - 0.78) ** 2 + (g / mag - 0.57) ** 2 + (b / mag - 0.44) ** 2);
      const skin = (1 - d - 0.8) / 0.2;
      if (skin <= 0 || mag < 60) continue; // not skin, or too dark to tell
      if (x >= rx0 && x < rx1 && y >= ry0 && y < ry1) inside += skin;
      else outside += skin;
    }
  }
  return { inside, outside };
}

/**
 * What cropperjs `getData()` returns for an unrotated, unflipped crop, plus
 * `frame`: the pixel size of the image the rectangle was measured in. The
 * crop tool and Review Book open a different rendition of the same photo
 * (the API's 2000px edit preview), so they rescale through
 * cropDataForImage() before using it.
 */
export function toCropperData(rect, frame = null) {
  if (!rect) return null;
  const data = {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    rotate: 0,
    scaleX: 1,
    scaleY: 1,
  };
  if (frame && isPositiveFinite(frame.width) && isPositiveFinite(frame.height)) {
    data.frame = { width: frame.width, height: frame.height };
  }
  return data;
}

/**
 * The crop rectangle in the pixels of an image that is naturalWidth ×
 * naturalHeight. Rectangles saved by the crop tool carry no `frame` and are
 * already in that image's pixels; the auto crop's carry the frame they were
 * measured in and are scaled across. Returns a cropperjs-shaped object
 * without `frame`, or null for no data.
 */
export function cropDataForImage(cropData, naturalWidth, naturalHeight) {
  if (!cropData || typeof cropData !== 'object') return null;
  const { frame, ...data } = cropData;
  if (
    !frame ||
    !isPositiveFinite(frame.width) ||
    !isPositiveFinite(frame.height) ||
    !isPositiveFinite(naturalWidth) ||
    !isPositiveFinite(naturalHeight)
  ) {
    return data;
  }
  const sx = naturalWidth / frame.width;
  const sy = naturalHeight / frame.height;
  return {
    ...data,
    x: data.x * sx,
    y: data.y * sy,
    width: data.width * sx,
    height: data.height * sy,
  };
}

function clamp(n, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(n, min), max);
}
